<template>
  <div class="min-h-screen bg-gray-100">
    <div v-if="!isMobile()" class="sticky top-0 z-50 bg-white shadow">
      <HeaderComponent 
        :cartItemCount="cart.length"
        :isLoggedIn="isLoggedIn"
        @toggle-cart="toggleCart"
        @login="toggleLogin"
        @search="applySearch"
        @sort="applySort"
      />
    </div>
    <div v-if="isMobile()" class="top-0 z-50 bg-white shadow">
      <HeaderComponent 
        :cartItemCount="cart.length"
        :isLoggedIn="isLoggedIn"
        @toggle-cart="toggleCart"
        @login="toggleLogin"
        @search="applySearch"
        @sort="applySort"
      />
    </div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div class="flex flex-col md:flex-row gap-2">
        <div v-if="isMobile()">
          <MobileFilter
            :brands="brands"
            :types="types"
            :categories="categories"
            @filter="applyFilter"
          />
        </div>
        <!-- Sidebar for desktop -->
        <SideBar
      v-if="!isMobile()"
      :brands="brands"
      :types="types"
      :categories="categories"
      @filter="applyFilter"
      class="w-full md:w-64 flex-shrink-0 md:sticky md:top-20 md:h-screen md:overflow-y-auto"
    />

        <!-- Product List -->
        <div class="flex-grow">
          <ProductList 
            :products="filteredProducts"
            :isMobile="isMobile()"
            @add-to-cart="addToCart"
            @show-detail="showProductDetail"
          />
        </div>
      </div>
    </div>

    <!-- Modals -->
    <CartModal 
      v-if="showCart"
      :cart="cart"
      :total="cartTotal"
      @close="showCart = false"
      @remove-from-cart="removeFromCart"
      @update-quantity="updateCartQuantity"
      @payment-confirmed="handlePaymentConfirmed"
    />

    <!-- Success Modal -->
    <SuccessModal
      v-if="showSuccessModal"
      @close="closeSuccessModal"
    />
    <ProductDetailModal
      v-if="selectedProduct"
      :show="!!selectedProduct"
      :product="selectedProduct"
      @close="selectedProduct = null"
      @add-to-cart="addToCart"
    />
    <LoginPromptModal
      v-if="showLoginPromptModal"
      :action="loginPromptAction"
      @close="showLoginPromptModal = false"
      @login="login"
    />
  </div>
</template>

<script>
import SideBar from '../components/SideBar.vue'
import ProductList from '../components/ProductList.vue'
import CartModal from '../components/CartModal.vue'
import ProductDetailModal from '../components/ProductDetailModal.vue'
import HeaderComponent from '@/components/HeaderComponent.vue'
import LoginPromptModal from '@/components/LoginPromptModal.vue'
import MobileFilter from '@/components/MobileFilter.vue'
import SuccessModal from '../components/PesanSukses.vue'
import { acService } from '../services/api'

export default {
  name: 'HomeView',
  components: {
    SideBar,
    ProductList,
    CartModal,
    ProductDetailModal,
    HeaderComponent,
    LoginPromptModal,
    MobileFilter,
    SuccessModal,
  },
  data() {
    return {
      products: [],
      brands: [],
      types: [],
      categories: [],
      cart: [],
      showCart: false,
      selectedProduct: null,
      filters: {
        brands: [],
        types: [],
        categories: [],
        pk: [0, 10],
        search: ''
      },
      sortBy: 'name',
      isLoggedIn: false,
      showLoginPromptModal: false,
      loginPromptAction: '',
      isLoading: false,
      error: null,
      showMobileFilter: false,
      showSuccessModal: false,
    }
  },
  watch: {
    '$route'(to) {
    if (!to.params.id) {
      this.selectedProduct = null
    }
  },
  '$route.query': {
    handler(query) {
      if (!query.cart) {
        this.showCart = false
      }
    },
    deep: true
  }
},
mounted() {
  // Check if there's a productId in query params when component mounts
  const productId = this.$route.query.productId
  if (productId) {
    const product = this.products.find(p => p.id === parseInt(productId))
    if (product) {
      this.selectedProduct = product
    }
  }
},
  computed: {
    filteredProducts() {
      return this.products.filter(product => {
        if (typeof product !== 'object' || product === null) {
          console.warn('Invalid product:', product);
          return false;
        }

        const brandMatch = this.filters.brands.length === 0 || 
          (product.brand && this.filters.brands.includes(product.brand.brand_name));
        
        const typeMatch = this.filters.types.length === 0 || 
          (product.type && this.filters.types.includes(product.type.type_name));
        
        const categoryMatch = this.filters.categories.length === 0 || 
          (product.category && this.filters.categories.includes(product.category.category_name));
        
        const pkMatch = !product.pk_power || 
          (parseFloat(product.pk_power) >= this.filters.pk[0] && 
           parseFloat(product.pk_power) <= this.filters.pk[1]);
        
        const searchMatch = !this.filters.search || (
          (product.title && product.title.toLowerCase().includes(this.filters.search.toLowerCase())) ||
          (this.filters.includeSellerName && product.user && 
           product.user.name && product.user.name.toLowerCase().includes(this.filters.search.toLowerCase())) ||
          (this.filters.includeSellerAddress && product.user && 
           product.user.address && product.user.address.toLowerCase().includes(this.filters.search.toLowerCase()))
        );
        
        return brandMatch && typeMatch && categoryMatch && pkMatch && searchMatch;
      }).sort((a, b) => {
        if (this.sortBy === 'name') {
          return (a.title || '').localeCompare(b.title || '');
        } else if (this.sortBy === 'price') {
          return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
        }
        return 0;
      });
    },
    cartTotal() {
      return this.cart.reduce((total, item) => total + (item.product.price * item.quantity), 0)
    }
  },
  created() {
    this.checkAuthStatus()
    this.fetchProducts()
    this.loadCartFromLocalStorage()
  },
  methods: {
    isMobile() {
      return screen.width <= 760;
    },
    removeFromCart(productId) {
    const index = this.cart.findIndex(item => item.product.id === productId)
    if (index !== -1) {
      this.cart.splice(index, 1)
      this.saveCartToLocalStorage()
      if (this.cart.length === 0) {
        const query = { ...this.$route.query }
        delete query.cart
        this.$router.replace({ query })
      }
    }
  },
    redirectToLogin() {
      if (this.$route.path !== '/login') {
        this.$router.push('/login')
      }
    },
    checkAuthStatus() {
  const token = localStorage.getItem('token')
  const isLoggedIn = localStorage.getItem('isLoggedIn')
  if (token && isLoggedIn === 'true') {
    this.isLoggedIn = true
  } else {
    this.isLoggedIn = false
    // Hapus redirect ke login di sini karena sudah ditangani oleh interceptor
    // this.$router.push('/login')  // <- hapus baris ini
  }
},
    async fetchProducts() {
      try {
        const response = await acService.getList();
        this.products = response.data.data || [];
        this.extractBrandsTypesAndCategories();
      } catch (error) {
        console.error('Failed to fetch products:', error);
      }
    },
    extractBrandsTypesAndCategories() {
      const brandSet = new Set();
      const typeSet = new Set();
      const categorySet = new Set();
      this.products.forEach(product => {
        if (product.brand && product.brand.brand_name) {
          brandSet.add(product.brand.brand_name);
        }
        if (product.type && product.type.type_name) {
          typeSet.add(product.type.type_name);
        }
        if (product.category && product.category.category_name) {
          categorySet.add(product.category.category_name);
        }
      });
      this.brands = Array.from(brandSet);
      this.types = Array.from(typeSet);
      this.categories = Array.from(categorySet);
    },
    addToCart(product, quantity = 1) {
      if (this.isLoggedIn) {
        const existingItem = this.cart.find(item => item.product.id === product.id)
        
        if (existingItem) {
          existingItem.quantity += quantity
        } else {
          this.cart.push({
            product: product,
            quantity: quantity
          })
        }
        
        this.showCart = true
        this.saveCartToLocalStorage()
      } else {
        this.showLoginPrompt('add items to the cart')
      }
    },
    toggleCart() {
  if (this.isLoggedIn) {
    if (!this.showCart) {
      this.$router.replace({ query: { ...this.$route.query, cart: 'open' } })
    } else {
      const query = { ...this.$route.query }
      delete query.cart
      this.$router.replace({ query })
    }
    this.showCart = !this.showCart
  } else {
    this.showLoginPrompt('view the cart')
  }
},

showProductDetail(product) {
  this.$router.push({ 
    name: 'product-detail',
    params: { id: product.id }
  })
  this.selectedProduct = product
},
    applyFilter(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      if (this.isMobile()) {
        this.toggleMobileFilter();
      }
    },
    updateCartQuantity(productId, change) {
      const item = this.cart.find(item => item.product.id === productId)
      if (item) {
        const newQuantity = item.quantity + change
        if (newQuantity > 0) {
          item.quantity = newQuantity
        } else {
          this.removeFromCart(productId)
        }
        this.saveCartToLocalStorage()
      }
    },
    async handlePaymentConfirmed() {
      try {
        // The actual API call is now handled in the CartModal component
        // Here we just need to update the local state
        this.cart = []
        this.saveCartToLocalStorage()
        this.showCart = false
        this.showSuccessModal = true
      } catch (error) {
        console.error('Error handling payment confirmation:', error)
        // Handle error (e.g., show error message to user)
      }
    },
    saveCartToLocalStorage() {
      localStorage.setItem('cart', JSON.stringify(this.cart))
    },
    loadCartFromLocalStorage() {
      const savedCart = localStorage.getItem('cart')
      if (savedCart) {
        this.cart = JSON.parse(savedCart)
      }
    },
    closeSuccessModal() {
      this.showSuccessModal = false
    },
    showLoginPrompt(action) {
      this.loginPromptAction = action
      this.showLoginPromptModal = true
    },
    Copylogin() {
  this.isLoggedIn = true
  this.showLoginPromptModal = false
  localStorage.setItem('isLoggedIn', 'true')
  // Pastikan token juga di-set saat login
  localStorage.setItem('token', 'your-token-value') 
},
logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('isLoggedIn')
      localStorage.removeItem('user')
      this.isLoggedIn = false
      this.cart = []
      this.$router.push('/login')
    },
    toggleLogin() {
      if (this.isLoggedIn) {
        this.logout()
      } else {
        this.redirectToLogin()
      }
    },
    applySearch({ query, includeSellerName, includeSellerAddress }) {
      this.filters.search = query;
      this.filters.includeSellerName = includeSellerName;
      this.filters.includeSellerAddress = includeSellerAddress;
    },
    applySort(sortBy) {
      this.sortBy = sortBy
    },
    toggleMobileFilter() {
      this.showMobileFilter = !this.showMobileFilter
    },
  },
}
</script>